import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  CubeIcon,
  FolderIcon,
  WrenchScrewdriverIcon,
  QueueListIcon,
  MagnifyingGlassIcon,
  ChevronRightIcon,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/formatters';
import { api } from '../services/authService';

const EquipamentosEspeciaisPage = () => {
  const [activeTab, setActiveTab] = useState('equipment'); // equipment, categories, kits, maintenance
  const [equipment, setEquipment] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesTree, setCategoriesTree] = useState([]);
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('equipment'); // equipment, category, kit
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Form states
  const [equipmentForm, setEquipmentForm] = useState({
    name: '',
    description: '',
    category_id: '',
    brand: '',
    model: '',
    specifications: '',
    unit_type: 'units',
    quantity_total: 1,
    quantity_available: 1,
    quantity_in_use: 0,
    quantity_maintenance: 0,
    purchase_price: '',
    daily_rate: '',
    monthly_rate: '',
    rental_price_daily: '',
    serial_number: '',
    barcode: '',
    condition: 'good',
    last_maintenance_date: '',
    next_maintenance_date: '',
    photo_url: '',
    notes: '',
    status: 'available'
  });

  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    parent_id: '',
    icon: '',
    display_order: 0
  });

  const [kitForm, setKitForm] = useState({
    name: '',
    description: '',
    vehicle_type: '',
    rental_type: '',
    items: []
  });

  // Estados para busca/filtro no modal de kit
  const [kitEquipmentSearch, setKitEquipmentSearch] = useState('');
  const [kitEquipmentCategory, setKitEquipmentCategory] = useState('all');

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);

      if (activeTab === 'equipment') {
        await Promise.all([
          loadEquipment(),
          loadCategories()
        ]);
      } else if (activeTab === 'categories') {
        await loadCategories();
      } else if (activeTab === 'kits') {
        await Promise.all([
          loadKits(),
          loadEquipment(),
          loadCategories()
        ]);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadEquipment = async () => {
    try {
      const response = await api.get('/api/auxiliary-equipment', {
        params: {
          category_id: filterCategory,
          status: filterStatus,
          search: searchTerm
        }
      });
      setEquipment(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar equipamentos:', error);
      toast.error('Erro ao carregar equipamentos');
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.get('/api/auxiliary-equipment/categories');
      const data = response.data.data;
      setCategories(data.flat || []);
      setCategoriesTree(data.tree || []);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      toast.error('Erro ao carregar categorias');
    }
  };

  const loadKits = async () => {
    try {
      const response = await api.get('/api/auxiliary-equipment/kits');
      setKits(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar kits:', error);
      toast.error('Erro ao carregar kits');
    }
  };

  // Equipment CRUD
  const handleEquipmentSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...equipmentForm,
        specifications: equipmentForm.specifications ? JSON.parse(equipmentForm.specifications) : {}
      };

      if (editingItem) {
        await api.put(`/api/auxiliary-equipment/${editingItem.id}`, payload);
        toast.success('Equipamento atualizado!');
      } else {
        await api.post('/api/auxiliary-equipment', payload);
        toast.success('Equipamento criado!');
      }

      loadEquipment();
      closeModal();
    } catch (error) {
      console.error('Erro ao salvar equipamento:', error);
      toast.error(error.response?.data?.error || 'Erro ao salvar equipamento');
    }
  };

  const handleDeleteEquipment = async (id) => {
    if (!confirm('Deseja realmente remover este equipamento?')) return;

    try {
      await api.delete(`/api/auxiliary-equipment/${id}`);
      toast.success('Equipamento removido!');
      loadEquipment();
    } catch (error) {
      console.error('Erro ao remover:', error);
      toast.error(error.response?.data?.error || 'Erro ao remover equipamento');
    }
  };

  // Category CRUD
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await api.put(`/api/auxiliary-equipment/categories/${editingItem.id}`, categoryForm);
        toast.success('Categoria atualizada!');
      } else {
        await api.post('/api/auxiliary-equipment/categories', categoryForm);
        toast.success('Categoria criada!');
      }

      loadCategories();
      closeModal();
    } catch (error) {
      console.error('Erro ao salvar categoria:', error);
      toast.error(error.response?.data?.error || 'Erro ao salvar categoria');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!confirm('Deseja realmente remover esta categoria?')) return;

    try {
      await api.delete(`/api/auxiliary-equipment/categories/${id}`);
      toast.success('Categoria removida!');
      loadCategories();
    } catch (error) {
      console.error('Erro ao remover:', error);
      toast.error(error.response?.data?.error || 'Erro ao remover categoria');
    }
  };

  // Kit CRUD
  const handleKitSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await api.put(`/api/auxiliary-equipment/kits/${editingItem.id}`, kitForm);
        toast.success('Kit atualizado!');
      } else {
        await api.post('/api/auxiliary-equipment/kits', kitForm);
        toast.success('Kit criado!');
      }

      loadKits();
      closeModal();
    } catch (error) {
      console.error('Erro ao salvar kit:', error);
      toast.error(error.response?.data?.error || 'Erro ao salvar kit');
    }
  };

  const handleDeleteKit = async (id) => {
    if (!confirm('Deseja realmente remover este kit?')) return;

    try {
      await api.delete(`/api/auxiliary-equipment/kits/${id}`);
      toast.success('Kit removido!');
      loadKits();
    } catch (error) {
      console.error('Erro ao remover:', error);
      toast.error(error.response?.data?.error || 'Erro ao remover kit');
    }
  };

  // Modal management
  const openEquipmentModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setEquipmentForm({
        ...item,
        specifications: item.specifications ? JSON.stringify(item.specifications, null, 2) : ''
      });
    } else {
      setEditingItem(null);
      setEquipmentForm({
        name: '',
        description: '',
        category_id: '',
        brand: '',
        model: '',
        specifications: '',
        unit_type: 'units',
        quantity_total: 1,
        quantity_available: 1,
        quantity_in_use: 0,
        quantity_maintenance: 0,
        purchase_price: '',
        daily_rate: '',
        monthly_rate: '',
        rental_price_daily: '',
        serial_number: '',
        barcode: '',
        condition: 'good',
        last_maintenance_date: '',
        next_maintenance_date: '',
        photo_url: '',
        notes: '',
        status: 'available'
      });
    }
    setModalType('equipment');
    setShowModal(true);
  };

  const openCategoryModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setCategoryForm({
        name: item.name,
        description: item.description || '',
        parent_id: item.parent_id || '',
        icon: item.icon || '',
        display_order: item.display_order || 0
      });
    } else {
      setEditingItem(null);
      setCategoryForm({
        name: '',
        description: '',
        parent_id: '',
        icon: '',
        display_order: 0
      });
    }
    setModalType('category');
    setShowModal(true);
  };

  const openKitModal = async (item = null) => {
    // Carregar TODOS os equipamentos (sem filtros) para o modal de kit
    try {
      const response = await api.get('/api/auxiliary-equipment');
      setEquipment(response.data.data || []);
      console.log(`✅ ${(response.data.data || []).length} equipamentos carregados para o modal de kit`);
    } catch (error) {
      console.error('Erro ao carregar equipamentos para kit:', error);
    }

    // Resetar filtros do modal de kit
    setKitEquipmentSearch('');
    setKitEquipmentCategory('all');

    if (item) {
      setEditingItem(item);
      loadKitDetails(item.id);
    } else {
      setEditingItem(null);
      setKitForm({
        name: '',
        description: '',
        vehicle_type: '',
        rental_type: '',
        items: []
      });
    }
    setModalType('kit');
    setShowModal(true);
  };

  const loadKitDetails = async (kitId) => {
    try {
      const response = await api.get(`/api/auxiliary-equipment/kits/${kitId}`);
      const kit = response.data.data;
      setKitForm({
        name: kit.name,
        description: kit.description || '',
        vehicle_type: kit.vehicle_type || '',
        rental_type: kit.rental_type || '',
        items: kit.items || []
      });
    } catch (error) {
      console.error('Erro ao carregar detalhes do kit:', error);
      toast.error('Erro ao carregar detalhes do kit');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType('equipment');
    setEditingItem(null);
  };

  const addKitItem = () => {
    setKitForm({
      ...kitForm,
      items: [...kitForm.items, { equipment_id: '', quantity: 1, is_mandatory: true }]
    });
  };

  const updateKitItem = (index, field, value) => {
    const updatedItems = [...kitForm.items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setKitForm({ ...kitForm, items: updatedItems });
  };

  const removeKitItem = (index) => {
    setKitForm({
      ...kitForm,
      items: kitForm.items.filter((_, i) => i !== index)
    });
  };

  // Funções auxiliares para manipular itens do kit via checkbox
  const isEquipmentInKit = (equipmentId) => {
    return kitForm.items.some(item => item.equipment_id === equipmentId);
  };

  const getKitItem = (equipmentId) => {
    return kitForm.items.find(item => item.equipment_id === equipmentId);
  };

  const toggleEquipmentInKit = (equipmentId, checked) => {
    if (checked) {
      // Adicionar ao kit
      setKitForm({
        ...kitForm,
        items: [...kitForm.items, { equipment_id: equipmentId, quantity: 1, is_mandatory: true }]
      });
    } else {
      // Remover do kit
      setKitForm({
        ...kitForm,
        items: kitForm.items.filter(item => item.equipment_id !== equipmentId)
      });
    }
  };

  const updateKitItemByEquipmentId = (equipmentId, field, value) => {
    setKitForm({
      ...kitForm,
      items: kitForm.items.map(item =>
        item.equipment_id === equipmentId
          ? { ...item, [field]: value }
          : item
      )
    });
  };

  // Render category tree
  const renderCategoryTree = (categories, level = 0) => {
    return categories.map((cat) => (
      <div key={cat.id} className="mb-2">
        <div
          className={`flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow ${level > 0 ? 'ml-8' : ''}`}
        >
          <div className="flex items-center gap-3">
            {cat.icon && <span className="text-2xl">{cat.icon}</span>}
            <div>
              <div className="font-medium text-gray-900 dark:text-white">{cat.name}</div>
              {cat.description && (
                <div className="text-sm text-gray-600 dark:text-gray-400">{cat.description}</div>
              )}
              <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                {cat.equipment_count || 0} equipamentos
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => openCategoryModal(cat)}
              className="p-2 bg-blue-100 dark:bg-blue-900/20 hover:bg-blue-200 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded transition-colors"
              title="Editar"
            >
              <PencilSquareIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleDeleteCategory(cat.id)}
              className="p-2 bg-red-100 dark:bg-red-900/20 hover:bg-red-200 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 rounded transition-colors"
              title="Remover"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
        {cat.children && cat.children.length > 0 && (
          <div className="mt-2">
            {renderCategoryTree(cat.children, level + 1)}
          </div>
        )}
      </div>
    ));
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.path || cat.name : 'Sem categoria';
  };

  const getStatusBadge = (status) => {
    const badges = {
      available: { text: 'Disponível', class: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' },
      in_use: { text: 'Em Uso', class: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' },
      maintenance: { text: 'Manutenção', class: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' },
      retired: { text: 'Aposentado', class: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300' }
    };
    return badges[status] || badges.available;
  };

  const getConditionBadge = (condition) => {
    const badges = {
      excellent: { text: 'Excelente', class: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' },
      good: { text: 'Bom', class: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' },
      fair: { text: 'Regular', class: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' },
      poor: { text: 'Ruim', class: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' }
    };
    return badges[condition] || badges.good;
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Equipamentos Especiais
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Gerencie equipamentos que acompanham os veículos conforme o tipo de aluguel
          </p>
        </div>

        <button
          onClick={() => {
            if (activeTab === 'equipment') openEquipmentModal();
            else if (activeTab === 'categories') openCategoryModal();
            else if (activeTab === 'kits') openKitModal();
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          {activeTab === 'equipment' && 'Novo Equipamento'}
          {activeTab === 'categories' && 'Nova Categoria'}
          {activeTab === 'kits' && 'Novo Kit'}
        </button>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('equipment')}
            className={`pb-3 px-4 font-medium transition-colors relative ${
              activeTab === 'equipment'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            <div className="flex items-center gap-2">
              <CubeIcon className="w-5 h-5" />
              Equipamentos
              <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                {equipment.length}
              </span>
            </div>
          </button>

          <button
            onClick={() => setActiveTab('categories')}
            className={`pb-3 px-4 font-medium transition-colors relative ${
              activeTab === 'categories'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            <div className="flex items-center gap-2">
              <FolderIcon className="w-5 h-5" />
              Categorias
              <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300">
                {categories.length}
              </span>
            </div>
          </button>

          <button
            onClick={() => setActiveTab('kits')}
            className={`pb-3 px-4 font-medium transition-colors relative ${
              activeTab === 'kits'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            <div className="flex items-center gap-2">
              <QueueListIcon className="w-5 h-5" />
              Kits Pré-configurados
              <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                {kits.length}
              </span>
            </div>
          </button>
        </div>
      </div>

      {/* Equipment Tab */}
      {activeTab === 'equipment' && (
        <>
          {/* Filters and Search */}
          <div className="mb-6 flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[300px]">
              <div className="relative">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyUp={(e) => e.key === 'Enter' && loadEquipment()}
                  placeholder="Buscar por nome, marca, modelo ou número de série..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            <select
              value={filterCategory}
              onChange={(e) => {
                setFilterCategory(e.target.value);
                loadEquipment();
              }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">Todas as categorias</option>
              {categories.filter(c => !c.parent_id).map((cat) => (
                <optgroup key={cat.id} label={cat.name}>
                  <option value={cat.id}>{cat.name}</option>
                  {categories.filter(sub => sub.parent_id === cat.id).map(sub => (
                    <option key={sub.id} value={sub.id}>└─ {sub.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>

            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                loadEquipment();
              }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">Todos os status</option>
              <option value="available">Disponível</option>
              <option value="in_use">Em Uso</option>
              <option value="maintenance">Manutenção</option>
              <option value="retired">Aposentado</option>
            </select>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {equipment.length}
              </div>
              <div className="text-sm text-blue-700 dark:text-blue-300">
                Total de Equipamentos
              </div>
            </div>

            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {equipment.reduce((sum, e) => sum + (e.quantity_available || 0), 0)}
              </div>
              <div className="text-sm text-green-700 dark:text-green-300">
                Unidades Disponíveis
              </div>
            </div>

            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {equipment.reduce((sum, e) => sum + (e.quantity_in_use || 0), 0)}
              </div>
              <div className="text-sm text-purple-700 dark:text-purple-300">
                Em Uso
              </div>
            </div>

            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
              <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {equipment.reduce((sum, e) => sum + (e.quantity_maintenance || 0), 0)}
              </div>
              <div className="text-sm text-yellow-700 dark:text-yellow-300">
                Em Manutenção
              </div>
            </div>

            <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4">
              <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                {formatCurrency(equipment.reduce((sum, e) => sum + (parseFloat(e.purchase_price) || 0), 0))}
              </div>
              <div className="text-sm text-indigo-700 dark:text-indigo-300">
                Valor Total Investido
              </div>
            </div>
          </div>

          {/* Equipment List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="spinner mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">Carregando...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {equipment.map((item) => {
                const statusBadge = getStatusBadge(item.status);
                const conditionBadge = getConditionBadge(item.condition);

                return (
                  <div
                    key={item.id}
                    className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white text-lg mb-1">
                          {item.name}
                        </h3>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {getCategoryName(item.category_id)}
                        </p>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${statusBadge.class}`}>
                          {statusBadge.text}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded ${conditionBadge.class}`}>
                          {conditionBadge.text}
                        </span>
                      </div>
                    </div>

                    {item.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                        {item.description}
                      </p>
                    )}

                    {(item.brand || item.model) && (
                      <div className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                        <span className="font-medium">{item.brand}</span>
                        {item.brand && item.model && ' - '}
                        <span>{item.model}</span>
                      </div>
                    )}

                    <div className="space-y-2 mb-4">
                      {item.serial_number && (
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-600 dark:text-gray-400">Nº Série:</span>
                          <span className="font-mono font-semibold text-gray-900 dark:text-white">
                            {item.serial_number}
                          </span>
                        </div>
                      )}

                      {item.rental_price_daily && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Diária:</span>
                          <span className="font-semibold text-green-600 dark:text-green-400">
                            {formatCurrency(item.rental_price_daily)}
                          </span>
                        </div>
                      )}

                      {item.monthly_rate && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Mensal:</span>
                          <span className="font-semibold text-green-600 dark:text-green-400">
                            {formatCurrency(item.monthly_rate)}
                          </span>
                        </div>
                      )}

                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Estoque:</span>
                        <span className="font-semibold text-blue-600 dark:text-blue-400">
                          {item.quantity_available || 0} / {item.quantity_total || 0} disponíveis
                        </span>
                      </div>

                      {item.quantity_in_use > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Em uso:</span>
                          <span className="font-semibold text-purple-600 dark:text-purple-400">
                            {item.quantity_in_use}
                          </span>
                        </div>
                      )}

                      {item.quantity_maintenance > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Manutenção:</span>
                          <span className="font-semibold text-yellow-600 dark:text-yellow-400">
                            {item.quantity_maintenance}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <button
                        onClick={() => openEquipmentModal(item)}
                        className="flex-1 p-2 bg-blue-100 dark:bg-blue-900/20 hover:bg-blue-200 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded transition-colors"
                        title="Editar"
                      >
                        <PencilSquareIcon className="w-5 h-5 mx-auto" />
                      </button>

                      <button
                        onClick={() => handleDeleteEquipment(item.id)}
                        className="flex-1 p-2 bg-red-100 dark:bg-red-900/20 hover:bg-red-200 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 rounded transition-colors"
                        title="Remover"
                      >
                        <TrashIcon className="w-5 h-5 mx-auto" />
                      </button>
                    </div>
                  </div>
                );
              })}

              {equipment.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <CubeIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">
                    Nenhum equipamento encontrado
                  </p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <>
          <div className="mb-6">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Organize seus equipamentos em categorias hierárquicas. Categorias pai podem ter subcategorias.
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="spinner mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">Carregando...</p>
            </div>
          ) : (
            <>
              {categoriesTree.length > 0 ? (
                renderCategoryTree(categoriesTree)
              ) : (
                <div className="text-center py-12">
                  <FolderIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">
                    Nenhuma categoria encontrada. Crie sua primeira categoria!
                  </p>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Kits Tab */}
      {activeTab === 'kits' && (
        <>
          <div className="mb-6">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Kits pré-configurados permitem agrupar equipamentos que geralmente são alugados juntos.
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="spinner mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">Carregando...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {kits.map((kit) => (
                <div
                  key={kit.id}
                  className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
                        {kit.name}
                      </h3>
                      {kit.rental_type && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {kit.rental_type}
                        </p>
                      )}
                    </div>
                    {kit.active && (
                      <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                        Ativo
                      </span>
                    )}
                  </div>

                  {kit.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      {kit.description}
                    </p>
                  )}

                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Itens no kit:</span>
                      <span className="font-semibold text-blue-600 dark:text-blue-400">
                        {kit.items_count || 0}
                      </span>
                    </div>

                    {kit.estimated_daily_cost && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Custo diário estimado:</span>
                        <span className="font-semibold text-green-600 dark:text-green-400">
                          {formatCurrency(kit.estimated_daily_cost)}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => openKitModal(kit)}
                      className="flex-1 p-2 bg-blue-100 dark:bg-blue-900/20 hover:bg-blue-200 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded transition-colors"
                      title="Editar"
                    >
                      <PencilSquareIcon className="w-5 h-5 mx-auto" />
                    </button>

                    <button
                      onClick={() => handleDeleteKit(kit.id)}
                      className="flex-1 p-2 bg-red-100 dark:bg-red-900/20 hover:bg-red-200 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 rounded transition-colors"
                      title="Remover"
                    >
                      <TrashIcon className="w-5 h-5 mx-auto" />
                    </button>
                  </div>
                </div>
              ))}

              {kits.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <QueueListIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">
                    Nenhum kit encontrado. Crie seu primeiro kit!
                  </p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              {/* Equipment Form */}
              {modalType === 'equipment' && (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                      {editingItem ? 'Editar Equipamento' : 'Novo Equipamento'}
                    </h3>
                    <button
                      onClick={closeModal}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>

                  <form onSubmit={handleEquipmentSubmit} className="space-y-6">
                    {/* Basic Information */}
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-4">Informações Básicas</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Nome *
                          </label>
                          <input
                            type="text"
                            value={equipmentForm.name}
                            onChange={(e) => setEquipmentForm({ ...equipmentForm, name: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="Ex: Tanque de Tinta 500L"
                            required
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Descrição
                          </label>
                          <textarea
                            value={equipmentForm.description}
                            onChange={(e) => setEquipmentForm({ ...equipmentForm, description: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            rows={3}
                            placeholder="Descrição detalhada do equipamento"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Categoria *
                          </label>
                          <select
                            value={equipmentForm.category_id}
                            onChange={(e) => setEquipmentForm({ ...equipmentForm, category_id: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            required
                          >
                            <option value="">Selecione uma categoria</option>
                            {categories.filter(c => !c.parent_id).map((cat) => (
                              <optgroup key={cat.id} label={cat.name}>
                                <option value={cat.id}>{cat.name}</option>
                                {categories.filter(sub => sub.parent_id === cat.id).map(sub => (
                                  <option key={sub.id} value={sub.id}>└─ {sub.name}</option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Unidade de Medida
                          </label>
                          <select
                            value={equipmentForm.unit_type}
                            onChange={(e) => setEquipmentForm({ ...equipmentForm, unit_type: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            <option value="units">Unidades</option>
                            <option value="liters">Litros</option>
                            <option value="hp">Cavalos (HP)</option>
                            <option value="kg">Quilogramas</option>
                            <option value="meters">Metros</option>
                            <option value="psi">PSI</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Marca
                          </label>
                          <input
                            type="text"
                            value={equipmentForm.brand}
                            onChange={(e) => setEquipmentForm({ ...equipmentForm, brand: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="Ex: Graco"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Modelo
                          </label>
                          <input
                            type="text"
                            value={equipmentForm.model}
                            onChange={(e) => setEquipmentForm({ ...equipmentForm, model: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="Ex: Contractor II"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Número de Série
                          </label>
                          <input
                            type="text"
                            value={equipmentForm.serial_number}
                            onChange={(e) => setEquipmentForm({ ...equipmentForm, serial_number: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="Ex: TT500-001"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Código de Barras
                          </label>
                          <input
                            type="text"
                            value={equipmentForm.barcode}
                            onChange={(e) => setEquipmentForm({ ...equipmentForm, barcode: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="Ex: 7891234567890"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Specifications (JSON) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Especificações Técnicas (JSON)
                      </label>
                      <textarea
                        value={equipmentForm.specifications}
                        onChange={(e) => setEquipmentForm({ ...equipmentForm, specifications: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                        rows={4}
                        placeholder='{"potencia": "15HP", "capacidade": "500L", "pressao_max": "10bar"}'
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Digite as especificações em formato JSON. Exemplo: {`{"capacidade": "500L", "material": "aço inox"}`}
                      </p>
                    </div>

                    {/* Quantities and Stock */}
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-4">Estoque</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Qtd Total *
                          </label>
                          <input
                            type="number"
                            value={equipmentForm.quantity_total}
                            onChange={(e) => setEquipmentForm({ ...equipmentForm, quantity_total: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            min="0"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Disponível *
                          </label>
                          <input
                            type="number"
                            value={equipmentForm.quantity_available}
                            onChange={(e) => setEquipmentForm({ ...equipmentForm, quantity_available: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            min="0"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Em Uso
                          </label>
                          <input
                            type="number"
                            value={equipmentForm.quantity_in_use}
                            onChange={(e) => setEquipmentForm({ ...equipmentForm, quantity_in_use: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            min="0"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Manutenção
                          </label>
                          <input
                            type="number"
                            value={equipmentForm.quantity_maintenance}
                            onChange={(e) => setEquipmentForm({ ...equipmentForm, quantity_maintenance: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            min="0"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Pricing */}
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-4">Valores</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Valor de Compra (R$)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={equipmentForm.purchase_price}
                            onChange={(e) => setEquipmentForm({ ...equipmentForm, purchase_price: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="0.00"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Diária (R$) *
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={equipmentForm.rental_price_daily || equipmentForm.daily_rate}
                            onChange={(e) => setEquipmentForm({ ...equipmentForm, rental_price_daily: e.target.value, daily_rate: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="0.00"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Mensal (R$) *
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={equipmentForm.monthly_rate}
                            onChange={(e) => setEquipmentForm({ ...equipmentForm, monthly_rate: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="0.00"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    {/* Status and Condition */}
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-4">Status e Condição</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Status *
                          </label>
                          <select
                            value={equipmentForm.status}
                            onChange={(e) => setEquipmentForm({ ...equipmentForm, status: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            required
                          >
                            <option value="available">Disponível</option>
                            <option value="in_use">Em Uso</option>
                            <option value="maintenance">Manutenção</option>
                            <option value="retired">Aposentado</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Condição *
                          </label>
                          <select
                            value={equipmentForm.condition}
                            onChange={(e) => setEquipmentForm({ ...equipmentForm, condition: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            required
                          >
                            <option value="excellent">Excelente</option>
                            <option value="good">Bom</option>
                            <option value="fair">Regular</option>
                            <option value="poor">Ruim</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Maintenance Dates */}
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-4">Manutenção</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Última Manutenção
                          </label>
                          <input
                            type="date"
                            value={equipmentForm.last_maintenance_date}
                            onChange={(e) => setEquipmentForm({ ...equipmentForm, last_maintenance_date: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Próxima Manutenção
                          </label>
                          <input
                            type="date"
                            value={equipmentForm.next_maintenance_date}
                            onChange={(e) => setEquipmentForm({ ...equipmentForm, next_maintenance_date: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Photo URL and Notes */}
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-4">Informações Adicionais</h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            URL da Foto
                          </label>
                          <input
                            type="text"
                            value={equipmentForm.photo_url}
                            onChange={(e) => setEquipmentForm({ ...equipmentForm, photo_url: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="https://exemplo.com/foto.jpg"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Observações
                          </label>
                          <textarea
                            value={equipmentForm.notes}
                            onChange={(e) => setEquipmentForm({ ...equipmentForm, notes: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            rows={3}
                            placeholder="Observações internas sobre o equipamento"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Buttons */}
                    <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <button
                        type="button"
                        onClick={closeModal}
                        className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                      >
                        {editingItem ? 'Atualizar' : 'Criar'}
                      </button>
                    </div>
                  </form>
                </>
              )}

              {/* Category Form */}
              {modalType === 'category' && (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                      {editingItem ? 'Editar Categoria' : 'Nova Categoria'}
                    </h3>
                    <button
                      onClick={closeModal}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>

                  <form onSubmit={handleCategorySubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Nome *
                      </label>
                      <input
                        type="text"
                        value={categoryForm.name}
                        onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Ex: Tanques e Reservatórios"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Descrição
                      </label>
                      <textarea
                        value={categoryForm.description}
                        onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        rows={3}
                        placeholder="Descrição da categoria"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Categoria Pai
                      </label>
                      <select
                        value={categoryForm.parent_id}
                        onChange={(e) => setCategoryForm({ ...categoryForm, parent_id: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="">Nenhuma (Categoria Principal)</option>
                        {categories.filter(c => !c.parent_id && (!editingItem || c.id !== editingItem.id)).map((cat) => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Selecione uma categoria pai para criar uma subcategoria
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Ícone (Emoji)
                      </label>
                      <input
                        type="text"
                        value={categoryForm.icon}
                        onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Ex: 🚛 🔧 📦"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Ordem de Exibição
                      </label>
                      <input
                        type="number"
                        value={categoryForm.display_order}
                        onChange={(e) => setCategoryForm({ ...categoryForm, display_order: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        min="0"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Categorias com ordem menor aparecem primeiro
                      </p>
                    </div>

                    <div className="flex justify-end gap-2 pt-4">
                      <button
                        type="button"
                        onClick={closeModal}
                        className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                      >
                        {editingItem ? 'Atualizar' : 'Criar'}
                      </button>
                    </div>
                  </form>
                </>
              )}

              {/* Kit Form */}
              {modalType === 'kit' && (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                      {editingItem ? 'Editar Kit' : 'Novo Kit'}
                    </h3>
                    <button
                      onClick={closeModal}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>

                  <form onSubmit={handleKitSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Nome *
                        </label>
                        <input
                          type="text"
                          value={kitForm.name}
                          onChange={(e) => setKitForm({ ...kitForm, name: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="Ex: Kit Caminhão de Pintura Completo"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Descrição
                        </label>
                        <input
                          type="text"
                          value={kitForm.description}
                          onChange={(e) => setKitForm({ ...kitForm, description: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="Descrição do kit"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Tipo de Veículo
                        </label>
                        <input
                          type="text"
                          value={kitForm.vehicle_type}
                          onChange={(e) => setKitForm({ ...kitForm, vehicle_type: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="Ex: truck"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Tipo de Aluguel
                        </label>
                        <input
                          type="text"
                          value={kitForm.rental_type}
                          onChange={(e) => setKitForm({ ...kitForm, rental_type: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="Ex: Caminhão de pintura"
                        />
                      </div>
                    </div>

                    {/* Itens do Kit - Grid com Checkboxes */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Selecione os Equipamentos do Kit
                      </label>

                      {/* Busca e Filtro */}
                      <div className="flex gap-3 mb-3">
                        <input
                          type="text"
                          placeholder="Buscar equipamento..."
                          value={kitEquipmentSearch}
                          onChange={(e) => setKitEquipmentSearch(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        />
                        <select
                          value={kitEquipmentCategory}
                          onChange={(e) => setKitEquipmentCategory(e.target.value)}
                          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        >
                          <option value="all">Todas Categorias</option>
                          {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>
                              {cat.icon ? `${cat.icon} ` : ''}{cat.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Grid de Equipamentos */}
                      <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {equipment
                            .filter(item => {
                              const matchesSearch = item.name.toLowerCase().includes(kitEquipmentSearch.toLowerCase()) ||
                                                    (item.brand && item.brand.toLowerCase().includes(kitEquipmentSearch.toLowerCase()));
                              const matchesCategory = kitEquipmentCategory === 'all' || item.category_id === kitEquipmentCategory;
                              return matchesSearch && matchesCategory;
                            })
                            .map(item => {
                              const isSelected = isEquipmentInKit(item.id);
                              const kitItem = getKitItem(item.id);

                              return (
                                <div
                                  key={item.id}
                                  className={`flex items-start gap-2 p-2 border rounded-lg transition-colors ${
                                    isSelected
                                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                      : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => toggleEquipmentInKit(item.id, e.target.checked)}
                                    className="mt-1 w-4 h-4 cursor-pointer text-blue-600 rounded"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm text-gray-900 dark:text-white truncate">
                                      {item.name}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      {item.brand || 'Sem marca'} {item.model ? `- ${item.model}` : ''}
                                    </p>
                                    {item.monthly_rate && (
                                      <p className="text-xs font-semibold text-green-600 dark:text-green-400">
                                        +R$ {Number(item.monthly_rate).toFixed(2)}/mês
                                      </p>
                                    )}

                                    {/* Controles de quantidade e obrigatório (só aparecem quando selecionado) */}
                                    {isSelected && (
                                      <div className="flex items-center gap-3 mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                                        <div className="flex items-center gap-1">
                                          <label className="text-xs text-gray-600 dark:text-gray-400">Qtd:</label>
                                          <input
                                            type="number"
                                            min="1"
                                            value={kitItem?.quantity || 1}
                                            onChange={(e) => updateKitItemByEquipmentId(item.id, 'quantity', parseInt(e.target.value) || 1)}
                                            className="w-14 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                          />
                                        </div>
                                        <label className="flex items-center gap-1 cursor-pointer">
                                          <input
                                            type="checkbox"
                                            checked={kitItem?.is_mandatory ?? true}
                                            onChange={(e) => updateKitItemByEquipmentId(item.id, 'is_mandatory', e.target.checked)}
                                            className="w-3 h-3 text-blue-600 rounded"
                                          />
                                          <span className="text-xs text-gray-600 dark:text-gray-400">Obrig.</span>
                                        </label>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                        </div>

                        {equipment.filter(item => {
                          const matchesSearch = item.name.toLowerCase().includes(kitEquipmentSearch.toLowerCase()) ||
                                                (item.brand && item.brand.toLowerCase().includes(kitEquipmentSearch.toLowerCase()));
                          const matchesCategory = kitEquipmentCategory === 'all' || item.category_id === kitEquipmentCategory;
                          return matchesSearch && matchesCategory;
                        }).length === 0 && (
                          <p className="text-center text-gray-500 dark:text-gray-400 py-8 text-sm">
                            Nenhum equipamento encontrado
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Resumo dos itens selecionados */}
                    {kitForm.items.length > 0 && (
                      <div className="bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Equipamentos Selecionados
                            </p>
                            <p className="text-lg font-bold text-gray-900 dark:text-white">
                              {kitForm.items.length} tipo(s) - {kitForm.items.reduce((sum, item) => sum + (parseInt(item.quantity) || 1), 0)} unidades
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Valor Mensal do Kit
                            </p>
                            <p className="text-lg font-bold text-green-600 dark:text-green-400">
                              R$ {equipment
                                .filter(eq => isEquipmentInKit(eq.id))
                                .reduce((sum, eq) => {
                                  const kitItem = getKitItem(eq.id);
                                  const quantity = kitItem?.quantity || 1;
                                  return sum + (Number(eq.monthly_rate || 0) * quantity);
                                }, 0)
                                .toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="flex justify-end gap-2 pt-4">
                      <button
                        type="button"
                        onClick={closeModal}
                        className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                      >
                        {editingItem ? 'Atualizar' : 'Criar'}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EquipamentosEspeciaisPage;
